import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, Button, Input, Label } from "@/components/ui";
import { Loader2, ArrowDownLeft, AlertCircle, CheckCircle2, X, Info } from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import { companyApi } from "@/services/api";

interface RedeemApplicationModalProps {
  open: boolean;
  onClose: () => void;
  appliedBalance: number; // em reais
  yieldAmount: number; // em reais
  onSuccess: () => void;
}

export function RedeemApplicationModal({
  open,
  onClose,
  appliedBalance,
  yieldAmount,
  onSuccess,
}: RedeemApplicationModalProps) {
  const [redeemType, setRedeemType] = useState<"all" | "yield" | "custom">("all");
  const [customAmount, setCustomAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [resultMessage, setResultMessage] = useState("");

  const totalBalance = appliedBalance + yieldAmount;

  // Formata valor para exibição com máscara de moeda (1.234,56)
  const formatCurrencyInput = (value: string): string => {
    const numbers = value.replace(/\D/g, "");
    if (!numbers) return "";
    const cents = parseInt(numbers, 10);
    const reais = cents / 100;
    return reais.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Converte valor formatado para centavos
  const parseCurrencyInput = (value: string): number => {
    if (!value) return 0;
    const normalized = value.replace(/\./g, "").replace(",", ".");
    return Math.round(parseFloat(normalized) * 100) || 0;
  };

  const getRedeemAmount = (): number => {
    switch (redeemType) {
      case "all":
        return Math.round(totalBalance * 100);
      case "yield":
        return Math.round(yieldAmount * 100);
      case "custom":
        return parseCurrencyInput(customAmount);
      default:
        return 0;
    }
  };

  const handleSubmit = async () => {
    const amountCents = getRedeemAmount();

    if (amountCents < 100) {
      setError("O valor mínimo para resgate é R$ 1,00");
      return;
    }

    if (amountCents > Math.round(totalBalance * 100)) {
      setError("O valor informado é maior que o saldo aplicado");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const result = await companyApi.dashboard.redeemApplication(
        amountCents,
        redeemType === "yield"
      );

      setSuccess(true);
      setResultMessage(
        `${formatCurrency(result.amount / 100)} foram transferidos para o saldo Disponível.` +
          (result.yieldAmount > 0
            ? ` (Principal: ${formatCurrency(result.principalAmount / 100)}, Rendimento: ${formatCurrency(result.yieldAmount / 100)})`
            : "")
      );
      onSuccess();
    } catch (err: any) {
      setError(err.message || "Erro ao resgatar aplicação. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setRedeemType("all");
    setCustomAmount("");
    setError("");
    setSuccess(false);
    setResultMessage("");
    onClose();
  };

  if (!open) return null;

  // Success state
  if (success) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="py-8">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8 text-green-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Resgate realizado!</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {resultMessage}
                </p>
              </div>
            </div>
            <div className="flex justify-center mt-6">
              <Button onClick={handleClose}>Fechar</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <Card className="w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-2">
            <ArrowDownLeft className="w-5 h-5" />
            <CardTitle>Resgatar Aplicação</CardTitle>
          </div>
          <Button variant="ghost" size="sm" onClick={handleClose}>
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>

        <CardContent className="space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <p className="text-sm">{error}</p>
            </div>
          )}

          {/* Saldos */}
          <div className="bg-muted rounded-lg p-4">
            <p className="text-sm text-muted-foreground">Total Aplicado</p>
            <p className="text-2xl font-bold">{formatCurrency(totalBalance)}</p>
            <div className="flex gap-4 mt-2 text-sm">
              <div>
                <span className="text-muted-foreground">Principal: </span>
                <span className="font-medium">{formatCurrency(appliedBalance)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Rendimento: </span>
                <span className="font-medium text-green-600">{formatCurrency(yieldAmount)}</span>
              </div>
            </div>
          </div>

          {/* Opções de resgate */}
          <div className="space-y-2">
            <Label>O que deseja resgatar?</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setRedeemType("all")}
                className={`p-3 rounded-lg border text-center transition-colors ${
                  redeemType === "all"
                    ? "border-primary bg-primary/10"
                    : "border-border bg-muted hover:border-primary/50"
                }`}
              >
                <p className="text-xs font-medium">Tudo</p>
                <p className="text-sm font-bold mt-1">{formatCurrency(totalBalance)}</p>
              </button>
              <button
                type="button"
                onClick={() => setRedeemType("yield")}
                disabled={yieldAmount <= 0}
                className={`p-3 rounded-lg border text-center transition-colors ${
                  redeemType === "yield"
                    ? "border-primary bg-primary/10"
                    : "border-border bg-muted hover:border-primary/50"
                } ${yieldAmount <= 0 ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <p className="text-xs font-medium">Só Rendimento</p>
                <p className="text-sm font-bold mt-1 text-green-600">{formatCurrency(yieldAmount)}</p>
              </button>
            </div>
          </div>

          {/* Valor Customizado */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setRedeemType("custom")}
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  redeemType === "custom" ? "border-primary" : "border-muted-foreground"
                }`}
              >
                {redeemType === "custom" && (
                  <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                )}
              </button>
              <Label htmlFor="customAmount" className="cursor-pointer" onClick={() => setRedeemType("custom")}>
                Ou digite um valor específico:
              </Label>
            </div>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                R$
              </span>
              <Input
                id="customAmount"
                placeholder="0,00"
                value={customAmount}
                onChange={(e) => setCustomAmount(formatCurrencyInput(e.target.value))}
                onFocus={() => setRedeemType("custom")}
                className="pl-10"
                inputMode="numeric"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Mínimo: R$ 1,00
            </p>
            {redeemType === "custom" && (
              <>
                {parseCurrencyInput(customAmount) > Math.round(totalBalance * 100) && (
                  <p className="text-xs text-destructive">Valor excede o saldo disponível</p>
                )}
              </>
            )}
          </div>

          {/* Info */}
          <div className="flex items-start gap-2 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg text-blue-700 dark:text-blue-300">
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <p className="text-xs">
              O valor será creditado no seu saldo Disponível. Depois você pode converter para USDC
              ou resgatar para conta bancária.
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={handleClose} disabled={loading}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} disabled={loading}>
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ArrowDownLeft className="w-4 h-4 mr-2" />
              )}
              Resgatar
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
